/**
 * SMB Plates - Shopify Theme JavaScript
 */

// Hero Slider
class HeroSlider {
  constructor(element) {
    this.slider = element;
    this.slides = this.slider.querySelectorAll('.hero-slider__slide');
    this.dots = this.slider.querySelectorAll('.hero-slider__dot');
    this.currentSlide = 0;
    this.autoplayInterval = null;
    
    this.init();
  }
  
  init() {
    if (this.slides.length <= 1) return;
    
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });
    
    this.startAutoplay();
  }
  
  goToSlide(index) {
    this.slides[this.currentSlide].classList.remove('active');
    this.dots[this.currentSlide].classList.remove('active');
    
    this.currentSlide = index;
    
    this.slides[this.currentSlide].classList.add('active');
    this.dots[this.currentSlide].classList.add('active');
    
    this.resetAutoplay();
  }
  
  nextSlide() {
    const next = (this.currentSlide + 1) % this.slides.length;
    this.goToSlide(next);
  }
  
  startAutoplay() {
    this.autoplayInterval = setInterval(() => this.nextSlide(), 5000);
  }
  
  resetAutoplay() {
    clearInterval(this.autoplayInterval);
    this.startAutoplay();
  }
}

// FAQ Accordion
class FAQAccordion {
  constructor(element) {
    this.container = element;
    this.items = this.container.querySelectorAll('.faq__item');
    
    this.init();
  }
  
  init() {
    if (this.container.dataset.faqInitialized === 'true') return;
    this.container.dataset.faqInitialized = 'true';

    this.items.forEach(item => {
      const question = item.querySelector('.faq__question');
      if (!question) return;
      question.addEventListener('click', () => this.toggleItem(item));
    });
  }
  
  toggleItem(item) {
    const isActive = item.classList.contains('active');
    
    // Close all items
    this.items.forEach(i => {
      i.classList.remove('active');
      const question = i.querySelector('.faq__question');
      const answer = i.querySelector('.faq__answer');

      if (question) {
        question.setAttribute('aria-expanded', 'false');
      }

      if (answer) {
        answer.style.maxHeight = null;
      }
    });
    
    // Open clicked item if it wasn't active
    if (!isActive) {
      item.classList.add('active');
      const question = item.querySelector('.faq__question');
      const answer = item.querySelector('.faq__answer');

      if (question) {
        question.setAttribute('aria-expanded', 'true');
      }

      if (answer) {
        answer.style.maxHeight = `${answer.scrollHeight}px`;
      }
    }
  }
}

// Testimonials Slider
class TestimonialsSlider {
  constructor(element) {
    this.slider = element;
    this.testimonials = this.slider.querySelectorAll('.testimonial');
    this.dots = this.slider.querySelectorAll('.testimonials__dot');
    this.prevBtn = this.slider.querySelector('.testimonials__arrow--prev');
    this.nextBtn = this.slider.querySelector('.testimonials__arrow--next');
    this.currentIndex = 0;
    
    this.init();
  }
  
  init() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }
    
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });
  }
  
  goTo(index) {
    this.testimonials[this.currentIndex].classList.remove('active');
    this.dots[this.currentIndex].classList.remove('active');
    
    this.currentIndex = index;
    
    this.testimonials[this.currentIndex].classList.add('active');
    this.dots[this.currentIndex].classList.add('active');
  }
  
  next() {
    const next = (this.currentIndex + 1) % this.testimonials.length;
    this.goTo(next);
  }
  
  prev() {
    const prev = (this.currentIndex - 1 + this.testimonials.length) % this.testimonials.length;
    this.goTo(prev);
  }
}

// Product Gallery
class ProductGallery {
  constructor(element) {
    this.gallery = element;
    this.mainImage = this.gallery.querySelector('.product-gallery__main-image');
    this.thumbs = Array.from(this.gallery.querySelectorAll('.product-gallery__thumb'));

    this.init();
  }

  init() {
    if (!this.mainImage || this.thumbs.length === 0) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.setActive(thumb));
    });

    this.setActive(this.thumbs[0]);
  }

  setActive(thumb) {
    const src = thumb.dataset.image;
    const alt = thumb.dataset.alt;

    if (src) {
      this.mainImage.src = src;
    }

    if (alt) {
      this.mainImage.alt = alt;
    }

    this.thumbs.forEach((item) => item.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }
}

// Product Variant Picker
class ProductVariantPicker {
  constructor(form) {
    this.form = form;
    this.variantSelect = form.querySelector('[data-variant-select]');
    this.optionInputs = Array.from(form.querySelectorAll('[data-variant-option-input]'));
    this.optionGroups = Array.from(form.querySelectorAll('.product-option[data-option-index]'));
    this.plateTypeInputs = Array.from(form.querySelectorAll('[data-plate-type-input]'));
    this.plateTypeToggle = form.querySelector('[data-plate-type-toggle]');
    this.uploadsSection = form.querySelector('[data-product-uploads]');
    this.uploadInputs = Array.from(form.querySelectorAll('[data-upload-input]'));
    this.priceRow = document.querySelector('.product-page__price-row');
    this.priceCurrent = document.querySelector('.product-page__price-current');
    this.priceCompare = document.querySelector('.product-page__price-compare');
    this.statusPill = document.querySelector('.product-page__status-pill');
    this.addToCart = form.querySelector('.product-page__add-to-cart');

    const variantsEl = document.querySelector('[data-product-variants]');
    const pricesEl = document.querySelector('[data-variant-prices]');

    this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    this.prices = pricesEl ? JSON.parse(pricesEl.textContent) : {};

    this.init();
  }

  init() {
    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.onOptionChange());
    });

    this.plateTypeInputs.forEach((input) => {
      input.addEventListener('change', () => this.updateProofRequirements());
    });

    if (this.variantSelect && this.optionInputs.length > 0) {
      this.onOptionChange();
    } else {
      this.updateProofRequirements();
    }
  }

  onOptionChange() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.variants.find((item) =>
      item.options.every((option, index) => option === selectedOptions[index])
    );

    this.updateProofRequirements();

    if (!variant) return;

    this.variantSelect.value = variant.id;
    this.updatePrice(variant);
    this.updateAvailability(variant);
  }

  getSelectedOptions() {
    const optionGroups = {};

    this.optionInputs.forEach((input) => {
      optionGroups[input.name] = optionGroups[input.name] || null;
      if (input.checked) {
        optionGroups[input.name] = input.value;
      }
    });

    return Object.keys(optionGroups)
      .sort()
      .map((key) => optionGroups[key]);
  }

  updateProofRequirements() {
    if (!this.uploadsSection || this.uploadInputs.length === 0) return;

    const isShowPlateSelection = this.isShowPlateSelection();
    this.uploadsSection.classList.toggle('is-hidden', isShowPlateSelection);

    this.uploadInputs.forEach((input) => {
      const shouldRequire = !isShowPlateSelection;
      input.disabled = isShowPlateSelection;
      input.required = shouldRequire;

      if (isShowPlateSelection) {
        input.classList.remove('is-invalid');
        input.setCustomValidity('');
      }
    });

    const errorEl = this.uploadsSection.querySelector('[data-upload-error]');
    if (errorEl && isShowPlateSelection) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  isShowPlateSelection() {
    const isShowValue = (value) => /\bshow\b/i.test(value || '');
    const isLegalValue = (value) => /\blegal\b/i.test(value || '');

    if (this.plateTypeToggle) {
      const checkedInput = this.plateTypeToggle.querySelector('[data-plate-type-input]:checked');
      return isShowValue((checkedInput && checkedInput.value) || '');
    }

    const showLegalGroup = this.optionGroups.find((group) => {
      const values = Array.from(group.querySelectorAll('[data-variant-option-input]')).map((input) => input.value || '');
      return values.some((value) => isShowValue(value)) && values.some((value) => isLegalValue(value));
    });

    if (!showLegalGroup) {
      return false;
    }

    const checkedInput = showLegalGroup.querySelector('.product-option__input:checked');
    return isShowValue((checkedInput && checkedInput.value) || '');
  }

  updatePrice(variant) {
    const priceData = this.prices[variant.id];
    if (!priceData || !this.priceRow || !this.priceCurrent) return;

    this.priceCurrent.textContent = priceData.price;

    if (priceData.has_compare) {
      if (!this.priceCompare) {
        this.priceCompare = document.createElement('span');
        this.priceCompare.className = 'product-page__price-compare';
        this.priceRow.appendChild(this.priceCompare);
      }
      this.priceCompare.textContent = priceData.compare_at_price;
      this.priceCompare.style.display = '';
    } else if (this.priceCompare) {
      this.priceCompare.style.display = 'none';
    }
  }

  updateAvailability(variant) {
    const priceData = this.prices[variant.id];
    if (!priceData || !this.priceRow) return;

    if (!priceData.available) {
      if (!this.statusPill) {
        this.statusPill = document.createElement('span');
        this.statusPill.className = 'product-page__status-pill is-out';
        this.statusPill.textContent = 'Sold out';
        this.priceRow.appendChild(this.statusPill);
      }
      this.statusPill.style.display = '';
      if (this.addToCart) {
        this.addToCart.disabled = true;
        this.addToCart.textContent = 'Sold out';
      }
    } else {
      if (this.statusPill) {
        this.statusPill.style.display = 'none';
      }
      if (this.addToCart) {
        this.addToCart.disabled = false;
        this.addToCart.textContent = 'Add to Cart';
      }
    }
  }
}

// Product Upload Validation
class ProductUploadValidator {
  constructor(form) {
    this.form = form;
    this.inputs = Array.from(form.querySelectorAll('[data-upload-input]'));
    this.errorEl = form.querySelector('[data-upload-error]');
    this.maxPerFile = 5 * 1024 * 1024; // 5MB per file
    this.maxTotal = 10 * 1024 * 1024; // 10MB total

    this.init();
  }

  init() {
    if (this.inputs.length === 0) return;

    this.inputs.forEach((input) => {
      input.addEventListener('change', () => this.validate());
    });

    this.form.addEventListener('submit', (event) => {
      if (!this.validate()) {
        event.preventDefault();
      }
    });
  }

  validate() {
    let totalSize = 0;
    const errors = [];

    this.inputs.forEach((input) => {
      input.classList.remove('is-invalid');
      input.setCustomValidity('');

      if (input.disabled) return;

      const file = input.files && input.files[0];
      if (!file) return;

      totalSize += file.size;

      if (file.size > this.maxPerFile) {
        input.classList.add('is-invalid');
        input.setCustomValidity('Each file must be 5MB or smaller.');
        errors.push(`${file.name} is larger than 5MB.`);
      }

      if (!this.isAllowedType(file)) {
        input.classList.add('is-invalid');
        input.setCustomValidity('Only JPG, PNG or PDF files are allowed.');
        errors.push(`${file.name} must be JPG, PNG or PDF.`);
      }
    });

    if (totalSize > this.maxTotal) {
      errors.push('Total upload size must be 10MB or less.');
      this.inputs.forEach((input) => {
        if (input.files && input.files[0]) {
          input.classList.add('is-invalid');
        }
      });
    }

    this.renderError(errors[0] || '');
    return errors.length === 0;
  }

  isAllowedType(file) {
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const fileName = file.name ? file.name.toLowerCase() : '';
    const hasAllowedExt = /\.(jpg|jpeg|png|pdf)$/.test(fileName);
    return allowedTypes.includes(file.type) || (file.type === '' && hasAllowedExt) || hasAllowedExt;
  }

  renderError(message) {
    if (!this.errorEl) return;

    if (!message) {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
      return;
    }

    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }
}

// Mobile Menu
class MobileMenu {
  constructor() {
    this.toggle = document.querySelector('.mobile-menu__toggle');
    this.overlay = document.querySelector('.mobile-menu__overlay');
    this.close = document.querySelector('.mobile-menu__close');
    
    this.init();
  }
  
  init() {
    if (!this.toggle || !this.overlay || !this.close) return;
    
    this.toggle.addEventListener('click', () => this.open());
    this.close.addEventListener('click', () => this.closeMenu());
    this.overlay.addEventListener('click', () => this.closeMenu());
  }
  
  open() {
    this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
  
  closeMenu() {
    this.overlay.classList.remove('active');
    document.body.style.overflow = '';
  }
}

// Cart Functions
class CartFunctions {
  constructor() {
    this.cartCount = document.querySelector('.cart-count');
    this.init();
  }
  
  init() {
    // Update cart count on page load
    this.updateCartCount();
    
    // Listen for cart updates
    document.addEventListener('cart:updated', () => this.updateCartCount());
  }
  
  async updateCartCount() {
    try {
      const response = await fetch(window.routes.cart_url + '.js');
      const cart = await response.json();
      
      if (this.cartCount) {
        this.cartCount.textContent = cart.item_count;
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
  
  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'error') {
        throw new Error(data.message);
      }
      
      // Dispatch cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }
}

// Initialize on DOM Ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize Hero Slider
  const heroSlider = document.querySelector('.hero-slider');
  if (heroSlider) {
    new HeroSlider(heroSlider);
  }
  
  // Initialize FAQ Accordion
  document.querySelectorAll('.faq__list').forEach((faqSection) => {
    new FAQAccordion(faqSection);
  });
  
  // Initialize Testimonials Slider
  const testimonialsSlider = document.querySelector('.testimonials__slider');
  if (testimonialsSlider) {
    new TestimonialsSlider(testimonialsSlider);
  }
  
  // Initialize Mobile Menu
  new MobileMenu();
  
  // Initialize Cart Functions
  new CartFunctions();
  
  // Add to Cart buttons
  document.querySelectorAll('[data-add-to-cart]').forEach(button => {
    button.addEventListener('click', async (e) => {
      e.preventDefault();
      
      const variantId = button.dataset.variantId;
      
      try {
        button.disabled = true;
        button.textContent = 'Adding...';
        
        await CartFunctions.prototype.addToCart(variantId);
        
        button.textContent = 'Added!';
        setTimeout(() => {
          button.textContent = 'Add to Cart';
          button.disabled = false;
        }, 2000);
      } catch (error) {
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = 'Add to Cart';
          button.disabled = false;
        }, 2000);
      }
    });
  });
});

// Shopify Predictive Search
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.product-gallery').forEach((gallery) => {
    new ProductGallery(gallery);
  });

  document.querySelectorAll('.product-page__form').forEach((form) => {
    new ProductVariantPicker(form);
    new ProductUploadValidator(form);
  });

  document.querySelectorAll('.product-registration__input').forEach((input) => {
    input.addEventListener('input', () => {
      input.value = input.value.toUpperCase();
    });
  });
});

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target;
  if (!root || !root.querySelectorAll) return;

  root.querySelectorAll('.faq__list').forEach((faqSection) => {
    new FAQAccordion(faqSection);
  });
});

// Shopify Predictive Search
document.addEventListener('DOMContentLoaded', () => {
  const searchInput = document.querySelector('.predictive-search__input');
  const searchResults = document.querySelector('.predictive-search__results');
  
  if (!searchInput || !searchResults) return;
  
  let debounceTimer;
  
  searchInput.addEventListener('input', (e) => {
    clearTimeout(debounceTimer);
    
    const query = e.target.value.trim();
    
    if (query.length < 2) {
      searchResults.innerHTML = '';
      searchResults.classList.remove('active');
      return;
    }
    
    debounceTimer = setTimeout(async () => {
      try {
        const response = await fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product`);
        const data = await response.json();
        
        if (data.resources && data.resources.results) {
          const products = data.resources.results.products || [];
          
          if (products.length > 0) {
            searchResults.innerHTML = products.map(product => `
              <a href="${product.url}" class="predictive-search__result">
                <img src="${product.image}" alt="${product.title}" class="predictive-search__image">
                <span class="predictive-search__title">${product.title}</span>
              </a>
            `).join('');
            searchResults.classList.add('active');
          } else {
            searchResults.innerHTML = '<div class="predictive-search__no-results">No results found</div>';
            searchResults.classList.add('active');
          }
        }
      } catch (error) {
        console.error('Search error:', error);
      }
    }, 300);
  });
  
  // Close search results when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.predictive-search')) {
      searchResults.classList.remove('active');
    }
  });
});
